import { ColDef, ValueGetterParams } from '@ag-grid-community/core'
import { NgGridComponents } from '@northfork/ag-grid-angular'
import { SourceDefinition } from '../models/source-definition.model'
import { MatchTargetKey, TARGET_META } from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'
import { MatchingService } from '../services/matching.service'
import {
  breaksCellRenderer,
  commentsCellRenderer,
  makeMatchCountRenderer,
  sourceCellRenderer,
  statusCellRenderer,
} from '../grid/renderers/renderers'

/**
 * Centres a computed column's CELLS (the breaks badges, match-count chips, comment icons). Applied as
 * `cellClass` only: every header in both grids is left-aligned, these included.
 */
const CENTER = 'wb-center-cell'

const sourceValue = (row: WorkbenchRow | undefined): string => {
  if (!row) return ''
  if (row.origin === 'api') return row.sourceLabel ?? ''
  if (row.origin === 'cust') return 'Custody'
  if (row.origin === 'trad') return 'Trading'
  return ''
}

/**
 * Catalog `field` -> the computed colId it supersedes.
 *
 * The API returns its own HELIOS group covering the same ground the UI builds itself. Presence,
 * order, label and default visibility come from the catalog; the renderer and value accessor stay
 * UI-owned, because the backend deliberately returns neither. This map is what stops the two
 * producing duplicate columns — note the match counts do not even collide by id (the API names
 * them after the source being matched, the UI keys them by target), so they need the alias.
 */
export const COMPUTED_COLUMN_ALIASES: Record<string, string> = {
  breaks: 'breaks',
  source: 'source',
  status: 'status',
  accountId: 'accountId',
  comments: 'comments',
  custodyMatches: 'matchCount:cust',
  tradingMatches: 'matchCount:trad',
  enfusionMatches: 'matchCount:api',
  clearwaterMatches: 'matchCount:cw',
}

/**
 * Catalog `field` -> the match target its count refers to.
 *
 * Exported so the grid can resolve a click on a real (fetched) match column to its target. On the
 * live-API path the match columns are built from the catalog entry and keep the API colIds
 * (`custodyMatches` etc.) rather than the UI's `matchCount:<target>` scheme, so the cross-filter
 * handler looks the target up here by colId instead of parsing a string prefix.
 */
export const MATCH_FIELD_TARGETS: Record<string, MatchTargetKey> = {
  custodyMatches: 'cust',
  tradingMatches: 'trad',
  enfusionMatches: 'api',
  clearwaterMatches: 'cw',
}

/**
 * Alias-map entries that ARE real server columns, so a filter or sort on them goes to `/query` like
 * any other promoted column (top level of `filter`, never `dynamicFilters`).
 *
 *  - `accountId` — promoted on both endpoints; §7 requires a user-applied Account ID filter to be
 *    sent verbatim.
 *  - the per-counterpart match COUNTS (helios !499, PVAL-26557) — `custodyMatches` /
 *    `tradingMatches` / `clearwaterMatches` on Enfusion, `enfusionMatches` / `clearwaterMatches` on
 *    Prometheus. They used to be keys inside an opaque `matchCounts` blob the server could neither
 *    filter nor sort; they are now physical `NUM` columns with a `NumberFilter` slot, sortable, so a
 *    "Custody Matches > 0" filter reaches the whole session. The UI still RENDERS them through
 *    `MatchingService.matchCount` (server count first), so the cells match what was filtered.
 *    `clearwaterMatches` is advertised but always 0 — Clearwater is never a flex counterpart yet.
 *
 * Only the catalog field NAMES are server-backed. The computed fallback colIds (`matchCount:cust`,
 * built when the catalog does not advertise the column) are not catalog fields, so the request
 * builder's allow-list drops them regardless.
 */
const SERVER_BACKED_ALIASES: ReadonlySet<string> = new Set([
  'accountId',
  ...Object.keys(MATCH_FIELD_TARGETS),
])

/**
 * The colIds whose VALUES this UI computes, so they must never appear in a request's `fields`,
 * `filter` or `sort` (PVAL-26129 §5).
 *
 * Neither per-source `fields` endpoint advertises these any more, so the catalog allow-list in
 * `grid/filters/request-filter.builder.ts` already excludes them on every path the backend controls.
 * This set is the belt to that braces, and it earns its keep in one case the allow-list cannot
 * cover: a catalog that DOES return a column of the same name. The Enfusion catalog is resolved live
 * from RPS per CW client, so another client's `tran_data` could legitimately carry a key called
 * `status` — that column would enter the allow-list, route dynamic, and resolve against a blob key
 * with nothing to do with the Status the user can see.
 *
 * Why these can't be server-authoritative at all: `status` reads `MatchingService.statusOf`, which
 * layers the optimistic `statusOverrides` written by a match / unmatch the user just performed on top
 * of the API's own verdict, and `source` / `breaks` / `comments` have no server column. Filtering or
 * sorting them server-side would contradict the action the user just took, not merely return
 * different rows — which is why the answer is "keep them off the wire", not "promote them".
 *
 * Derived from {@link COMPUTED_COLUMN_ALIASES} rather than hand-written, taking both its KEYS (the
 * catalog field names) and its VALUES (the computed colIds the UI keys by target) so both
 * column-building paths are covered and the set cannot drift as the alias map grows — minus
 * {@link SERVER_BACKED_ALIASES}.
 */
export const CLIENT_ONLY_FIELDS: ReadonlySet<string> = new Set(
  [
    ...Object.keys(COMPUTED_COLUMN_ALIASES),
    ...Object.values(COMPUTED_COLUMN_ALIASES),
  ].filter(field => !SERVER_BACKED_ALIASES.has(field)),
)

/**
 * On-screen disclosure that a computed column's filter AND sort are PAGE-scoped (PVAL-26129 §5).
 *
 * After this ticket every CATALOG column's filter and sort are evaluated by the backend across the
 * whole session, while these columns — whose values this UI computes and which therefore never reach
 * `fields`/`filter`/`sort` — keep filtering and sorting client-side over the one bounded page that is
 * loaded. Two columns side by side now answer a filter over different populations, and nothing on
 * screen said so; a README note cannot reach the user holding the filter open, so the disclosure goes
 * on the header where the control is.
 *
 * `headerTooltip` renders natively with no tooltip component, because the shared grid config sets
 * `enableBrowserTooltips: true` (`libs/ag-grid-config/src/lib/extend-grid-options.ts:232`).
 *
 * Deliberately scoped to these columns and to `externalReferenceSource` (see `grid/col-def-factory.ts`)
 * — this is a caveat on the surfaces whose behaviour actually diverges, not the start of a tooltip
 * programme across the catalog.
 */
export const COMPUTED_COLUMN_FILTER_SORT_TOOLTIP =
  'Computed by Helios: filtering and sorting this column apply only to the rows already loaded, not to the whole session.'

/**
 * The same disclosure for a computed column that offers no filter at all (`filter: false`), so only
 * its sort is page-scoped. Saying "filtering" on a column with no filter control would be its own
 * small lie, which is why this is a second constant rather than one shared string.
 */
export const COMPUTED_COLUMN_SORT_ONLY_TOOLTIP =
  'Computed by Helios: sorting this column applies only to the rows already loaded, not to the whole session.'

/**
 * Rendering behaviour for a catalog column the UI owns the presentation of, or `undefined` for an
 * ordinary data column. Merged over the type-driven ColDef so the catalog still supplies colId,
 * header and visibility.
 *
 * The page-scope disclosure is stamped here AND in {@link buildComputedColumns}: a column can reach
 * the grid through either path (catalog entry + override, or the computed fallback), so a disclosure
 * on one path only would be present or absent depending on whether the catalog happened to advertise
 * the column.
 */
export function heliosColumnOverride(
  field: string,
  matching: MatchingService,
): Partial<ColDef> | undefined {
  const target = MATCH_FIELD_TARGETS[field]
  if (target) {
    // A catalogued count is a real server column (helios !499), so its filter and sort reach the whole
    // session — hence the Northfork number filter (whose model the request converter reads, and which
    // the Filters modal lists) and NO page-scope tooltip. Only the rendering stays UI-owned.
    return {
      width: 120,
      sortable: true,
      filter: NgGridComponents.numberFilter,
      cellClass: CENTER,
      cellRenderer: makeMatchCountRenderer(TARGET_META[target].color),
      valueGetter: (params: ValueGetterParams) =>
        params.data ? matching.matchCount(params.data, target) : 0,
      type: undefined,
      valueFormatter: undefined,
      comparator: undefined,
      filterValueGetter: undefined,
    }
  }

  switch (field) {
    case 'breaks':
      return {
        width: 96,
        sortable: true,
        filter: false,
        headerTooltip: COMPUTED_COLUMN_SORT_ONLY_TOOLTIP,
        cellClass: CENTER,
        cellRenderer: breaksCellRenderer,
        valueGetter: (params: ValueGetterParams) =>
          params.data
            ? params.data.breaks.high * 1000 + params.data.breaks.medium
            : 0,
        type: undefined,
        valueFormatter: undefined,
        comparator: undefined,
        filterValueGetter: undefined,
      }
    case 'source':
      return {
        width: 120,
        filter: 'agSetColumnFilter',
        headerTooltip: COMPUTED_COLUMN_FILTER_SORT_TOOLTIP,
        cellRenderer: sourceCellRenderer,
        valueGetter: (params: ValueGetterParams) => sourceValue(params.data),
      }
    case 'status':
      return {
        width: 130,
        filter: 'agSetColumnFilter',
        headerTooltip: COMPUTED_COLUMN_FILTER_SORT_TOOLTIP,
        cellRenderer: statusCellRenderer,
        valueGetter: (params: ValueGetterParams) =>
          params.data ? matching.statusOf(params.data) : '',
      }
    case 'accountId':
      // NO page-scope disclosure here, and the omission is deliberate: `accountId` is genuinely
      // promoted on both endpoints (NUM/DEFAULT on Enfusion), so it is excluded from
      // CLIENT_ONLY_FIELDS above and a user-applied Account ID filter IS sent to the server and IS
      // session-scoped (§7). It only looks computed because the UI owns its narrow width and reads
      // it off the row's own `accountId`. Do not "tidy" a tooltip back in — it would tell the user
      // the opposite of what happens.
      return {
        width: 104,
        filter: 'agSetColumnFilter',
        valueGetter: (params: ValueGetterParams) =>
          params.data?.accountId ?? '',
      }
    case 'comments':
      return {
        width: 96,
        sortable: true,
        filter: false,
        headerTooltip: COMPUTED_COLUMN_SORT_ONLY_TOOLTIP,
        cellClass: CENTER,
        cellRenderer: commentsCellRenderer,
        valueGetter: (params: ValueGetterParams) => params.data?.comments ?? 0,
        type: undefined,
        valueFormatter: undefined,
        comparator: undefined,
        filterValueGetter: undefined,
      }
    default:
      return undefined
  }
}

/**
 * The leading, computed columns common to the workbench: Breaks, Source, Status, one Match-count
 * column per target source, Account ID and (where the source supports it) Comments.
 *
 * These are a fallback for when the catalog does not supply them — no focused account, a failed
 * fetch, or a source the backend has not catalogued (CW). When the catalog does return its HELIOS
 * group, {@link COMPUTED_COLUMN_ALIASES} suppresses the duplicates and
 * {@link heliosColumnOverride} carries the rendering across.
 */
export function buildComputedColumns(
  source: SourceDefinition,
  matching: MatchingService,
): ColDef[] {
  const columns: ColDef[] = []

  columns.push({
    colId: 'breaks',
    headerName: 'Breaks',
    width: 96,
    sortable: true,
    filter: false,
    headerTooltip: COMPUTED_COLUMN_SORT_ONLY_TOOLTIP,
    cellClass: CENTER,
    cellRenderer: breaksCellRenderer,
    valueGetter: (params: ValueGetterParams) =>
      params.data
        ? params.data.breaks.high * 1000 + params.data.breaks.medium
        : 0,
  })

  if (source.key === 'api' || source.key === 'prom') {
    columns.push({
      colId: 'source',
      headerName: 'Source',
      width: 120,
      filter: 'agSetColumnFilter',
      headerTooltip: COMPUTED_COLUMN_FILTER_SORT_TOOLTIP,
      cellRenderer: sourceCellRenderer,
      valueGetter: (params: ValueGetterParams) => sourceValue(params.data),
    })
  }

  columns.push({
    colId: 'status',
    headerName: 'Status',
    width: 130,
    filter: 'agSetColumnFilter',
    headerTooltip: COMPUTED_COLUMN_FILTER_SORT_TOOLTIP,
    cellRenderer: statusCellRenderer,
    valueGetter: (params: ValueGetterParams) =>
      params.data ? matching.statusOf(params.data) : '',
  })

  // One match-count column per target the SOURCE declares — which is why the disclosure is stamped
  // inside this iteration rather than against a hand-listed set of four field names. `matchTargets`
  // is source-dependent (Clearwater declares fewer), so a literal list would either miss a column or
  // name one that is never built.
  source.matchTargets.forEach(target => {
    columns.push({
      colId: `matchCount:${target}`,
      headerName: `${TARGET_META[target].label} Matches`,
      width: 120,
      sortable: true,
      filter: 'agNumberColumnFilter',
      headerTooltip: COMPUTED_COLUMN_FILTER_SORT_TOOLTIP,
      cellClass: CENTER,
      cellRenderer: makeMatchCountRenderer(TARGET_META[target].color),
      valueGetter: (params: ValueGetterParams) =>
        params.data ? matching.matchCount(params.data, target) : 0,
    })
  })

  // No disclosure on Account ID — see the carve-out comment in `heliosColumnOverride`: its filter is
  // genuinely sent to the server and is session-scoped.
  columns.push({
    colId: 'accountId',
    headerName: 'Account ID',
    width: 104,
    filter: 'agSetColumnFilter',
    valueGetter: (params: ValueGetterParams) => params.data?.accountId ?? '',
  })

  if (source.hasComments) {
    columns.push({
      colId: 'comments',
      headerName: 'Comments',
      width: 96,
      sortable: true,
      filter: false,
      headerTooltip: COMPUTED_COLUMN_SORT_ONLY_TOOLTIP,
      cellClass: CENTER,
      cellRenderer: commentsCellRenderer,
      valueGetter: (params: ValueGetterParams) => params.data?.comments ?? 0,
    })
  }

  return columns
}
